import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  IconButton,
  InputBase,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DataGrid,
  GridPagination,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

const NAVY = "#123B5D";
const PAGE_BG = "#F4F7FB";
const BORDER = "#DCE4EC";
const MUTED = "#5B6B7A";

export const MasterPage = ({ children }) => (
  <>
    <GlobalStyles
      styles={{
        ".select__menu-portal": {
          zIndex: "2000 !important",
        },
        ".select__menu-portal .select__menu": {
          zIndex: "2000 !important",
        },
        ".select__menu": {
          zIndex: "2000 !important",
        },
      }}
    />
    <Box sx={{ width: "100%", p: { xs: 1, md: 2 }, backgroundColor: PAGE_BG }}>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: "12px",
          borderColor: BORDER,
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
        }}
      >
        {children}
      </Paper>
    </Box>
  </>
);

export const MasterToolbar = ({ actions, search }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    flexWrap="wrap"
    gap={1.5}
    sx={{ px: 2, py: 1.5 }}
  >
    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
      {actions}
    </Stack>
    {search}
  </Stack>
);

const BUTTON_STYLES = {
  add: {
    icon: <AddIcon />,
    sx: {
      backgroundColor: "#2E9E6B",
      color: "#FFFFFF",
      "&:hover": { backgroundColor: "#278A5C" },
    },
  },
  update: {
    icon: <EditOutlinedIcon />,
    sx: {
      backgroundColor: "#F5A524",
      color: "#FFFFFF",
      "&:hover": { backgroundColor: "#E39718" },
    },
  },
  danger: {
    icon: <DeleteOutlineIcon />,
    sx: {
      backgroundColor: "#D64545",
      color: "#FFFFFF",
      "&:hover": { backgroundColor: "#B93838" },
    },
  },
  primary: {
    icon: null,
    sx: {
      backgroundColor: NAVY,
      color: "#FFFFFF",
      "&:hover": { backgroundColor: "#0E2F4A" },
    },
  },
  outline: {
    icon: <FileDownloadOutlinedIcon />,
    sx: {
      backgroundColor: "#FFFFFF",
      color: NAVY,
      border: "1px solid #C9D3DD",
      "&:hover": { backgroundColor: "#F1F5F9", borderColor: "#B3C0CD" },
    },
  },
};

export const MasterButton = ({
  variant = "outline",
  tooltip,
  icon,
  children,
  disabled,
  ...rest
}) => {
  const style = BUTTON_STYLES[variant];
  const button = (
    <Button
      disableElevation
      startIcon={(icon !== undefined ? icon : style.icon) || undefined}
      disabled={disabled}
      {...rest}
      sx={{
        height: 36,
        px: 1.75,
        borderRadius: "8px",
        textTransform: "none",
        fontSize: 13.5,
        fontWeight: 600,
        boxShadow: "none",
        "& .MuiButton-startIcon": { mr: 0.75, "& svg": { fontSize: 18 } },
        "&.Mui-disabled": { opacity: 0.55 },
        ...style.sx,
      }}
    >
      {children}
    </Button>
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="top">
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
};

export const MasterSearch = ({
  value,
  onChange,
  placeholder = "Search...",
  tooltip = "Search by All",
  inputRef,
}) => (
  <Tooltip title={tooltip} placement="left">
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: { xs: "100%", sm: 260 },
        height: 36,
        px: 1.25,
        borderRadius: "8px",
        border: "1px solid #C9D3DD",
        backgroundColor: "#FFFFFF",
        transition: "border-color .15s, box-shadow .15s",
        "&:focus-within": {
          borderColor: NAVY,
          boxShadow: "0 0 0 3px rgba(18,59,93,0.12)",
        },
      }}
    >
      <SearchIcon sx={{ fontSize: 18, color: "#8A949E" }} />
      <InputBase
        value={value}
        onChange={onChange}
        inputRef={inputRef}
        placeholder={placeholder}
        inputProps={{ "aria-label": tooltip }}
        sx={{ flex: 1, fontSize: 13.5 }}
      />
    </Box>
  </Tooltip>
);

const MasterGridFooter = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      minHeight: 44,
      px: 1,
      backgroundColor: "#F1F5F9",
      borderTop: "1px solid #E3E8ED",
      "& .MuiButton-root": {
        color: MUTED,
        fontSize: 12.5,
        fontWeight: 500,
        textTransform: "none",
      },
      "& .MuiTablePagination-root": { color: MUTED, overflow: "hidden" },
      "& .MuiTablePagination-toolbar": {
        minHeight: 40,
        display: "flex",
        alignItems: "center",
      },
      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
        {
          margin: 0,
          lineHeight: 1.5,
          fontSize: 12.5,
        },
      "& .MuiTablePagination-input, & .MuiInputBase-root": {
        margin: "0 8px",
        fontSize: 12.5,
        alignSelf: "center",
      },
      "& .MuiTablePagination-select": {
        paddingTop: 0,
        paddingBottom: 0,
        display: "flex",
        alignItems: "center",
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
    </Box>
    <GridPagination />
  </Box>
);

const gridSx = {
  border: 0,
  borderRadius: 0,
  fontSize: 13.5,
  color: "#1B2733",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: NAVY,
    color: "#FFFFFF",
    borderBottom: 0,
    fontSize: 13,
  },
  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600 },
  "& .MuiDataGrid-columnSeparator": { display: "none" },
  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
    { outline: "none" },
  "& .MuiDataGrid-iconButtonContainer .MuiSvgIcon-root, & .MuiDataGrid-menuIcon .MuiSvgIcon-root, & .MuiDataGrid-sortIcon":
    { color: "#FFFFFF" },
  "& .MuiDataGrid-columnHeader": { px: 2 },
  "& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitleContainer": {
    justifyContent: "flex-start",
  },
  "& .MuiDataGrid-cell": {
    px: 2,
    borderBottom: "1px solid #EEF2F6",
    justifyContent: "flex-start",
    textAlign: "left",
  },
  "& .MuiDataGrid-row:hover": { backgroundColor: "#F5F9FD" },
  "& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: "#EAF2FB",
    "&:hover": { backgroundColor: "#E1EDF9" },
  },
  "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell": {
    color: NAVY,
    fontWeight: 500,
  },
  "& .MuiCheckbox-root": { color: "#9FB3C6" },
  "& .MuiCheckbox-root.Mui-checked": { color: NAVY },
};

export const MasterGrid = ({ sx, components, ...props }) => (
  <Box sx={{ width: "100%", height: "calc(100vh - 230px)", minHeight: 420 }}>
    <DataGrid
      components={{ Footer: MasterGridFooter, ...components }}
      sx={{ ...gridSx, ...sx }}
      {...props}
    />
  </Box>
);

const dialogPaperSx = {
  borderRadius: "14px",
  overflow: "hidden",
  border: `1px solid ${BORDER}`,
  boxShadow: "0 20px 50px rgba(10,30,50,0.25)",
  backgroundColor: "#FFFFFF",
};

const guardClose = (onHide) => (_event, reason) => {
  if (reason === "backdropClick") return;
  if (onHide) onHide();
};

const masterFormSx = {
  "& .popupmodal-maindiv": {
    width: "100%",
    boxSizing: "border-box",
    px: {
      xs: 1.5,
      sm: 2,
    },
    py: {
      xs: 1.5,
      sm: 2,
    },
    backgroundColor: "#FFFFFF",
  },

  "& .popupmodal-maindiv .form-group": {
    width: "100%",
    boxSizing: "border-box",
    marginBottom: "16px",
  },

  "& .popupmodal-maindiv .col-lg-12": {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    paddingLeft: 0,
    paddingRight: 0,
  },

  "& .popupmodal-maindiv label": {
    display: "block",
    marginBottom: "6px",
    color: "#33485C",
    fontSize: "12.5px",
    fontWeight: 600,
    lineHeight: 1.35,
  },

  "& .popupmodal-maindiv input.form-control, & .popupmodal-maindiv select.form-control, & .popupmodal-maindiv textarea.form-control":
    {
      width: "100%",
      minWidth: 0,
      height: "40px",
      minHeight: "40px",
      boxSizing: "border-box",
      padding: "0 12px",
      border: "1px solid #CBD5DF",
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
      color: "#1B2733",
      fontFamily: "inherit",
      fontSize: "13.5px",
      fontWeight: 400,
      lineHeight: "40px",
      outline: "none",
      boxShadow: "none",
      transition: "border-color 150ms ease, box-shadow 150ms ease",
    },

  "& .popupmodal-maindiv input.form-control::placeholder, & .popupmodal-maindiv textarea.form-control::placeholder":
    {
      color: "#8996A3",
      opacity: 1,
      fontSize: "13.5px",
      fontWeight: 400,
    },

  "& .popupmodal-maindiv input.form-control:hover, & .popupmodal-maindiv select.form-control:hover, & .popupmodal-maindiv textarea.form-control:hover":
    {
      borderColor: "#AEBBC8",
    },

  "& .popupmodal-maindiv input.form-control:focus, & .popupmodal-maindiv select.form-control:focus, & .popupmodal-maindiv textarea.form-control:focus":
    {
      borderColor: `${NAVY} !important`,
      boxShadow: "0 0 0 3px rgba(18,59,93,0.10) !important",
      outline: "none !important",
    },

  "& .popupmodal-maindiv input[type='datetime-local']": {
    display: "block",
    width: "100%",
    minWidth: 0,
    height: "40px",
    minHeight: "40px",
    boxSizing: "border-box",
    padding: "0 12px",
    border: "1px solid #CBD5DF",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    color: "#1B2733",
    fontFamily: "inherit",
    fontSize: "13.5px",
    fontWeight: 400,
    lineHeight: "40px",
    outline: "none",
    boxShadow: "none",
    transition: "border-color 150ms ease, box-shadow 150ms ease",
    cursor: "pointer",
  },

  "& .popupmodal-maindiv input[type='datetime-local']:hover": {
    borderColor: "#AEBBC8",
  },

  "& .popupmodal-maindiv input[type='datetime-local']:focus": {
    borderColor: `${NAVY} !important`,
    boxShadow: "0 0 0 3px rgba(18,59,93,0.10) !important",
    outline: "none !important",
  },

  "& .popupmodal-maindiv input[type='datetime-local']::-webkit-calendar-picker-indicator":
    {
      width: "18px",
      height: "18px",
      opacity: 0.65,
      cursor: "pointer",
    },

  "& .popupmodal-maindiv .input-error, & .popupmodal-maindiv .form-control.input-error":
    {
      borderColor: "#D64545 !important",
    },

  "& .popupmodal-maindiv .input-error:focus, & .popupmodal-maindiv .form-control.input-error:focus":
    {
      borderColor: "#D64545 !important",
      boxShadow: "0 0 0 3px rgba(214,69,69,0.10) !important",
      outline: "none !important",
    },

  "& .popupmodal-maindiv .select__control": {
    width: "100%",
    minHeight: "40px",
    height: "40px",
    boxSizing: "border-box",
    borderRadius: "8px",
    border: "1px solid #CBD5DF",
    backgroundColor: "#FFFFFF",
    boxShadow: "none",
    transition: "border-color 150ms ease, box-shadow 150ms ease",
  },

  "& .popupmodal-maindiv .select__control:hover": {
    borderColor: "#AEBBC8",
  },

  "& .popupmodal-maindiv .select__control--is-focused": {
    borderColor: `${NAVY} !important`,
    boxShadow: "0 0 0 3px rgba(18,59,93,0.10) !important",
  },

  "& .popupmodal-maindiv .select__value-container": {
    height: "38px",
    padding: "0 12px",
  },

  "& .popupmodal-maindiv .select__placeholder": {
    margin: 0,
    color: "#8996A3",
    fontSize: "13.5px",
    fontWeight: 400,
  },

  "& .popupmodal-maindiv .select__single-value": {
    margin: 0,
    color: "#1B2733",
    fontSize: "13.5px",
    fontWeight: 400,
  },

  "& .popupmodal-maindiv .select__input-container": {
    margin: 0,
    padding: 0,
    fontSize: "13.5px",
  },

  "& .popupmodal-maindiv .select__input-container input": {
    height: "auto",
    minHeight: "auto",
    padding: 0,
    border: 0,
    borderRadius: 0,
    boxShadow: "none",
    background: "transparent",
    fontSize: "13.5px",
  },

  "& .popupmodal-maindiv .select__indicators": {
    height: "38px",
  },

  "& .popupmodal-maindiv .select__dropdown-indicator": {
    color: "#98A4AF",
    padding: "8px",
    "&:hover": {
      color: NAVY,
    },
  },

  "& .popupmodal-maindiv .select__clear-indicator": {
    color: "#A6B0BA",
    padding: "8px",
    "&:hover": {
      color: "#687684",
    },
  },

  "& .popupmodal-maindiv .select__menu": {
    zIndex: "2000 !important",
    marginTop: "4px",
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid #DCE4EC",
    boxShadow: "0 10px 24px rgba(15,35,55,0.14)",
  },

  "& .popupmodal-maindiv .select__option": {
    minHeight: "36px",
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    color: "#1B2733",
    backgroundColor: "#FFFFFF",
    fontSize: "13px",
    cursor: "pointer",
  },

  "& .popupmodal-maindiv .select__option--is-focused": {
    backgroundColor: "#F4F8FB",
  },

  "& .popupmodal-maindiv .select__option--is-selected": {
    backgroundColor: "#EAF2FB",
    color: NAVY,
    fontWeight: 600,
  },

  "& .popupmodal-maindiv .error-message": {
    display: "block",
    marginTop: "5px",
    color: "#D64545",
    fontSize: "11px",
    fontWeight: 500,
    lineHeight: 1.35,
  },

  "& .popupmodal-maindiv .bottombtn": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    width: "100%",
    boxSizing: "border-box",
    marginTop: "4px",
    paddingTop: "16px",
    borderTop: "1px solid #EEF2F6",
  },

  "& .popupmodal-maindiv .bottombtn .btn": {
    minWidth: "82px",
    height: "38px",
    padding: "0 18px",
    borderRadius: "8px",
    fontFamily: "inherit",
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: 1,
    boxShadow: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 150ms ease, border-color 150ms ease",
  },

  "& .popupmodal-maindiv .bottombtn .btn-success": {
    backgroundColor: "#2E9E6B",
    color: "#FFFFFF",
    border: "1px solid #2E9E6B",
  },

  "& .popupmodal-maindiv .bottombtn .btn-success:hover": {
    backgroundColor: "#278A5C",
    borderColor: "#278A5C",
    color: "#FFFFFF",
  },

  "& .popupmodal-maindiv .bottombtn .btn-danger": {
    backgroundColor: "#D64545",
    color: "#FFFFFF",
    border: "1px solid #D64545",
  },

  "& .popupmodal-maindiv .bottombtn .btn-danger:hover": {
    backgroundColor: "#B93838",
    borderColor: "#B93838",
    color: "#FFFFFF",
  },

  "& .popupmodal-maindiv .bottombtn .btn:disabled": {
    opacity: 0.55,
    cursor: "not-allowed",
  },

  "@media (max-width:600px)": {
    "& .popupmodal-maindiv": {
      px: 1.25,
      py: 1.5,
    },

    "& .popupmodal-maindiv .bottombtn": {
      justifyContent: "stretch",
    },

    "& .popupmodal-maindiv .bottombtn .btn": {
      flex: 1,
    },
  },
  "@media (max-width:899.95px)": {
    "& .popupmodal-maindiv .bottombtn": {
      position: "sticky",
      bottom: 0,
      zIndex: 2,
      backgroundColor: "#FFFFFF",
      paddingBottom: "max(12px, env(safe-area-inset-bottom))",
    },
  },
};
export const MasterDialog = ({
  open,
  onHide,
  title,
  children,
  maxWidth = "sm",
  contentPadding = 0,
  fullScreenOnMobile = true, // set false on any dialog that should stay a popup
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // < 900px
  const fullScreen = fullScreenOnMobile && isMobile;

  return (
    <Dialog
      open={Boolean(open)}
      onClose={guardClose(onHide)}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={maxWidth}
      scroll="paper"
      PaperProps={{
        sx: fullScreen
          ? {
              ...dialogPaperSx,
              borderRadius: 0,
              border: 0,
              boxShadow: "none",
              m: 0,
              width: "100%",
              maxWidth: "100%",
              height: "100dvh",
              maxHeight: "100dvh",
            }
          : {
              ...dialogPaperSx,
              maxHeight: "calc(100vh - 48px)",
            },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          minHeight: 54,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: { xs: 2, sm: 2.5 },
          py: 1.25,
          pt: fullScreen ? "max(10px, env(safe-area-inset-top))" : 1.25,
          backgroundColor: NAVY,
          color: "#FFFFFF",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          flexShrink: 0,
        }}
      >
        <Typography
          component="h2"
          sx={{
            m: 0,
            fontSize: {
              xs: 15,
              sm: 16,
            },
            fontWeight: 600,
            lineHeight: 1.3,
            color: "#FFFFFF",
          }}
        >
          {title}
        </Typography>

        <IconButton
          onClick={onHide}
          aria-label="Close"
          title="Close"
          size="small"
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            color: "rgba(255,255,255,0.88)",
            borderRadius: "8px",
            "&:hover": {
              color: "#FFFFFF",
              backgroundColor: "rgba(255,255,255,0.10)",
            },
          }}
        >
          <CloseIcon
            sx={{
              fontSize: 19,
            }}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: contentPadding,
          backgroundColor: "#FFFFFF",
          overflowY: "auto",
          flex: 1,
          ...masterFormSx,
          "&.MuiDialogContent-root": {
            p: contentPadding,
          },
          "&::-webkit-scrollbar": {
            width: 6,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#F8FAFC",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#CBD5E1",
            borderRadius: 10,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#AEBBC8",
          },
        }}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};

export const MasterDialogActions = ({ children }) => (
  <DialogActions
    sx={{
      gap: 1,
      px: 2.5,
      py: 1.5,
      backgroundColor: "#F8FAFC",
      borderTop: "1px solid #EEF2F6",
    }}
  >
    {children}
  </DialogActions>
);

export const MasterConfirmDialog = ({
  show,
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
}) => (
  <Dialog
    open={Boolean(show)}
    onClose={guardClose(onCancel)}
    fullWidth
    maxWidth="xs"
    PaperProps={{
      sx: {
        width: "100%",
        maxWidth: 460,
        borderRadius: "16px",
        overflow: "hidden",
        backgroundColor: "#FFFFFF",
        border: `1px solid ${BORDER}`,
        boxShadow: "0 24px 60px rgba(10,30,50,0.24)",
        "@media (max-width:600px)": {
          mx: 1.5,
          borderRadius: "14px",
        },
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        px: {
          xs: 1.5,
          sm: 2,
        },
        pt: {
          xs: 1.5,
          sm: 2,
        },
        pb: 1.5,
      }}
    >
      <Box
        sx={{
          width: 76,
          height: 76,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          backgroundColor: "#FFF4D9",
          color: "#C88719",
          border: "1px solid #F4DFB1",
          boxShadow: "0 4px 12px rgba(184,121,31,0.10)",
        }}
      >
        <WarningAmberOutlinedIcon
          sx={{
            fontSize: 40,
          }}
        />
      </Box>

      <Typography
        component="h2"
        sx={{
          mt: 1.25,
          fontSize: {
            xs: 18,
            sm: 19,
          },
          fontWeight: 700,
          lineHeight: 1.3,
          color: NAVY,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          mt: 1,
          maxWidth: 340,
          fontSize: {
            xs: 13.5,
            sm: 14,
          },
          lineHeight: 1.6,
          color: MUTED,
        }}
      >
        {message}
      </Typography>
    </Box>

    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 1.25,
        px: {
          xs: 2.5,
          sm: 4,
        },
        py: 2,
        backgroundColor: "#F8FAFC",
        borderTop: "1px solid #E9EEF3",
      }}
    >
      <MasterButton
        variant="outline"
        icon={null}
        onClick={onCancel}
        sx={{
          minWidth: 96,
          height: 38,
        }}
      >
        {cancelLabel}
      </MasterButton>

      <MasterButton
        variant="primary"
        icon={null}
        onClick={onConfirm}
        sx={{
          minWidth: 96,
          height: 38,
        }}
      >
        {confirmLabel}
      </MasterButton>
    </Box>
  </Dialog>
);
